import { useSyncExternalStore } from 'react';

/**
 * Seen-state for the "What's new" release-notes feed.
 *
 * LocalStorage-backed, framework-agnostic, mirrors the density-store
 * pattern (try/catch-guarded storage access + useSyncExternalStore).
 * Stores the newest `publishedAt` the user has seen; the header dot
 * lights up when the feed's newest note is newer. Client-only on
 * purpose — no server schema, no API, no migration.
 */
const STORAGE_KEY = 'ct-whats-new-last-seen';

/** A stored value only counts if it parses as a timestamp — garbage reads as "nothing seen". */
function readFromStorage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw !== null && !Number.isNaN(Date.parse(raw)) ? raw : null;
  } catch {
    return null;
  }
}

let lastSeen: string | null = readFromStorage();
const listeners = new Set<() => void>();

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

export function getLastSeenPublishedAt(): string | null {
  return lastSeen;
}

/**
 * Record the newest `publishedAt` as seen — called when the dialog opens.
 * A storage write failure (quota / private mode) is swallowed: the
 * in-memory state still updates for this session.
 */
export function markSeen(publishedAt: string): void {
  if (lastSeen === publishedAt) return;
  lastSeen = publishedAt;
  try {
    localStorage.setItem(STORAGE_KEY, publishedAt);
  } catch {
    /* quota / private mode — in-memory state still updated */
  }
  notifyListeners();
}

/**
 * Whether the feed holds notes the user has not seen. An empty/unloaded
 * feed (null newest) is never unread, so the dot cannot flash for users
 * with nothing published. A missing or unparsable stored value counts as
 * "nothing seen yet", so any real note reads as unread.
 */
export function isUnread(
  newestPublished: string | null,
  lastSeenValue: string | null,
): boolean {
  if (newestPublished === null) return false;
  const newestTime = Date.parse(newestPublished);
  if (Number.isNaN(newestTime)) return false;
  if (lastSeenValue === null) return true;
  const seenTime = Date.parse(lastSeenValue);
  return Number.isNaN(seenTime) || newestTime > seenTime;
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/** React hook — re-renders when the seen marker changes (dialog opened). */
export function useLastSeenPublishedAt(): string | null {
  return useSyncExternalStore(subscribe, getLastSeenPublishedAt, () => null);
}

/** Test-only helper — re-reads storage and drops subscribers so tests can reset the module singleton. */
export function _resetSeenStoreForTests(): void {
  lastSeen = readFromStorage();
  listeners.clear();
}
